/**
 * TipTap extension that overlays diff-highlight bands on the editor doc
 * via ProseMirror decorations.
 *
 * Decorations are visual-only — they do NOT modify the document model.
 * That's load-bearing here: injecting `<mark>` tags into the markdown
 * source (the previous approach) breaks the markdown parser whenever a
 * mark falls inside structured syntax like a table cell or code fence.
 * Decorations sidestep parsing entirely; they ride alongside the doc as
 * a separate visual layer.
 *
 * @module components/artifacts/diff-highlight-extension
 */

import { Extension } from "@tiptap/core";
import type { Editor } from "@tiptap/core";
import { Plugin, PluginKey } from "prosemirror-state";
import { Decoration, DecorationSet } from "prosemirror-view";

/** Plugin state key used to address transactions that update decorations. */
export const diffHighlightPluginKey = new PluginKey<DecorationSet>("diffHighlight");

/** Inclusive `from` / exclusive `to` doc-position range for one band. */
export interface DiffRange {
  from: number;
  to: number;
}

/**
 * Meta payload set on transactions that mutate the decoration state. The
 * extension's `apply` reads this and either rebuilds the decoration set
 * from a new range list or clears it entirely.
 */
interface DiffHighlightMeta {
  ranges?: DiffRange[];
  clear?: boolean;
}

/**
 * Empty extension that registers the decoration-holding plugin. No
 * commands or input rules — callers drive it via the `setDiffHighlight`
 * / `clearDiffHighlight` helpers below.
 */
export const DiffHighlightExtension = Extension.create({
  name: "diffHighlight",

  addProseMirrorPlugins() {
    return [
      new Plugin<DecorationSet>({
        key: diffHighlightPluginKey,
        state: {
          init: () => DecorationSet.empty,
          apply(tr, oldSet) {
            const meta = tr.getMeta(diffHighlightPluginKey) as DiffHighlightMeta | undefined;
            if (meta?.clear) return DecorationSet.empty;
            if (meta?.ranges) {
              const decorations = meta.ranges
                .filter((r) => r.from < r.to)
                .map((r) =>
                  Decoration.inline(r.from, r.to, { class: "artifact-diff-decoration" }),
                );
              return DecorationSet.create(tr.doc, decorations);
            }
            // No meta — keep existing decorations, but remap their
            // positions through any document changes. This makes
            // decorations survive user edits during the fade window.
            return oldSet.map(tr.mapping, tr.doc);
          },
        },
        props: {
          decorations(state) {
            return diffHighlightPluginKey.getState(state) ?? DecorationSet.empty;
          },
        },
      }),
    ];
  },
});

/** Install or replace the decoration set on the editor. */
export function setDiffHighlight(editor: Editor, ranges: DiffRange[]): void {
  const tr = editor.state.tr.setMeta(diffHighlightPluginKey, { ranges });
  editor.view.dispatch(tr);
}

/** Remove all diff highlight decorations. */
export function clearDiffHighlight(editor: Editor): void {
  const tr = editor.state.tr.setMeta(diffHighlightPluginKey, { clear: true });
  editor.view.dispatch(tr);
}
